import React from 'react';
import { Tag, TagLabel } from '@chakra-ui/react';
import {
  schemeFor, glowFor, statusCategory, humanizeStatus,
} from '../theme/statusColor';
import { useAppearance } from '../theme/AppearanceProvider';

// A status badge coloured by meaning (item 69). Glassy tinted pill with a soft
// glow on the Aurora skin; a flat, solid badge on the Accountant skin. Shared by
// the grid status column and the status-change menu so the colour is defined once.

interface StatusPillProps {
  label?: string;
  isVoid?: boolean;
  // item 553 — optional uniform width so a ledger's status column reads as one clean stack
  // (every pill sized to the widest label). Default keeps the size-to-content behaviour.
  minW?: string;
  // trailing content (e.g. the menu chevron when the pill is a MenuButton)
  children?: React.ReactNode;
}

const StatusPill: React.FC<StatusPillProps> = ({
  label, isVoid, minW, children,
}) => {
  const { aurora } = useAppearance();
  const scheme = schemeFor(label, isVoid);
  const cat = statusCategory(label, isVoid);
  // item 553 — a voided row just reads "VOID" (its prior status is noise here, Chad).
  const text = isVoid ? 'VOID' : humanizeStatus(label);

  return (
    <Tag
      size="sm"
      colorScheme={scheme}
      variant="subtle"
      borderRadius="xl"
      px={2}
      py={1}
      fontSize="0.68rem"
      fontWeight="bold"
      letterSpacing="0.04em"
      textTransform="uppercase"
      maxW="100%"
      minW={minW}
      justifyContent="center"
      boxShadow={aurora ? `0 0 12px ${glowFor(label, isVoid)}` : 'none'}
      // a faint ring keeps the flat (Accountant) pill legible without a glow
      border={aurora ? undefined : '1px solid'}
      borderColor={aurora ? undefined : `${scheme}.300`}
      opacity={cat === 'void' ? 0.85 : 1}
    >
      {/* a long status (e.g. "Approved Deposited") wraps at the SPACE only — never
          mid-word — and is capped at two tidy lines (item 85) */}
      <TagLabel
        whiteSpace="normal"
        textAlign="center"
        lineHeight="1.15"
        noOfLines={2}
        sx={{ wordBreak: 'normal', overflowWrap: 'normal', hyphens: 'none' }}
      >
        {text}
      </TagLabel>
      {children}
    </Tag>
  );
};

StatusPill.defaultProps = {
  label: '',
  isVoid: false,
  minW: undefined,
  children: undefined,
};

export default StatusPill;
